// ─────────────────────────────────────────────────────────────────────────
// useLiveLocationBroadcast.ts
// Continuously reads the driver's REAL device GPS via
// navigator.geolocation.watchPosition() and writes it to the job record
// (localStorage 'rs_jobs', or a real backend if wired up later) so the
// admin tracking map always reflects the driver's actual, live position —
// never a simulated/interpolated one.
//
// Location permission is requested exactly ONCE — the first time this
// device opens the driver's job-sheet link (watchPosition triggers the
// browser's permission prompt on its very first call, then never again for
// this origin). From that point on, tracking runs continuously for as long
// as the link stays open, regardless of the job's status — it always
// reflects the real position of whichever device/link is being used.
//
// Writes are throttled to avoid hammering storage/state on every GPS tick,
// but the first fix is written immediately so tracking starts right away.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';

export interface LiveLocationPing {
  lat: number;
  lng: number;
  /** ISO-8601 timestamp of when this fix was taken. */
  updatedAt: string;
  /** Horizontal accuracy radius in meters, as reported by the device (pos.coords.accuracy). Lower = more precise. */
  accuracy: number;
}

export type LiveLocationStatus =
  | 'idle'          // not enabled yet
  | 'watching'       // actively receiving GPS fixes
  | 'unsupported'    // device/browser has no geolocation API
  | 'denied';        // user denied location permission

export interface UseLiveLocationBroadcastResult {
  status: LiveLocationStatus;
  /** Most recent real fix sent, or null if none yet. */
  lastPing: LiveLocationPing | null;
}

// Minimum time between storage writes. Real GPS fixes can arrive faster
// than this (esp. with enableHighAccuracy); we don't need to persist every
// single one to keep the admin map effectively real-time.
const MIN_WRITE_INTERVAL_MS = 3_000; // 3 วินาที — ใช้เพื่อให้แอดมินเห็นตำแหน่ง real-time


/**
 * @param enabled   Whether tracking should be active right now — pass
 *                  `Boolean(job)` so it starts the moment the driver's
 *                  link loads a valid job record (permission is requested
 *                  once at that point) and keeps running for the whole
 *                  session, independent of job status.
 * @param onLocation Called with each real fix that's actually written
 *                  (already throttled) — hook this up to updateJob().
 */
export function useLiveLocationBroadcast(
  enabled: boolean,
  onLocation: (ping: LiveLocationPing) => void,
): UseLiveLocationBroadcastResult {
  const [status, setStatus] = useState<LiveLocationStatus>('idle');
  const [lastPing, setLastPing] = useState<LiveLocationPing | null>(null);
  const lastWriteAtRef = useRef(0);
  const watchIdRef = useRef<number | null>(null);
  // Keep the latest callback in a ref so the watch effect doesn't need to
  // restart (and drop GPS lock) every time the caller's callback identity changes.
  const onLocationRef = useRef(onLocation);
  onLocationRef.current = onLocation;

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }

    if (!('geolocation' in navigator)) {
      setStatus('unsupported');
      return;
    }

    setStatus('watching');
    lastWriteAtRef.current = 0; // force an immediate write on the first real fix

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        const ping: LiveLocationPing = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          updatedAt: new Date().toISOString(),
          accuracy: pos.coords.accuracy,
        };

        if (now - lastWriteAtRef.current < MIN_WRITE_INTERVAL_MS) return;
        lastWriteAtRef.current = now;

        setLastPing(ping);
        onLocationRef.current(ping);
      },
      (err) => {
        // PERMISSION_DENIED = 1 (per the Geolocation API spec)
        setStatus(err.code === 1 ? 'denied' : 'unsupported');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15_000,
      },
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled]);

  return { status, lastPing };
}
